import { useState, useEffect } from 'react';
import { HelpData } from './Help.props';
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';
import toast from 'react-hot-toast';

const useHelp = () => {
    const [helpData, setHelpData] = useState<HelpData>({
        faq: [{ question: '', answer: '' }],
        contactUs: [{ image: '', name: '', url: '' }],
    });
    const [formData, setFormData] = useState<FormData>(() => new FormData());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await ApiService({
                    method: "GET",
                    endpoint: apiPaths.help,
                });
                console.log("Help response: ", response);
                setHelpData(response||{
                    faq: [{ question: '', answer: '' }],
                    contactUs: [{ image: '', name: '', url: '' }],
                });
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const handleChange = (section: 'faq' | 'contactUs', index: number, field: string, value: string) => {
        const updatedSection = helpData[section].map((item: any, i: number) =>
            i === index ? { ...item, [field]: value } : item
        );
        setHelpData({ ...helpData, [section]: updatedSection });
    };

    const handleAdd = (section: 'faq' | 'contactUs') => {
        const newItem = section === 'faq' ? { question: '', answer: '' } : { image: '', name: '', url: '' };
        setHelpData({ ...helpData, [section]: [...helpData[section], newItem] });
    };

    const handleDelete = (section: 'faq' | 'contactUs', index: number) => {
        if (Array.isArray(helpData[section])) {
            const updatedSection = (helpData[section] as any[]).filter((_: any, i: number) => i !== index);
            setHelpData({ ...helpData, [section]: updatedSection });
        }
    };

    const toBase64 = (file: File) => {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve((reader.result as string).split(',')[1]); // Extract Base64 string
            reader.onerror = (error) => reject(error);
        });
    };

    const getBase64 = async () => {
        const base64Files: Record<string, { mimetype: string; data: string }[]> = {};

        for (const [key, value] of (formData as any).entries()) {
            if (value instanceof File) {
                const base64String = await toBase64(value);
                const mimetype = value.type;

                if (!base64Files[key]) {
                    base64Files[key] = [];
                }
                base64Files[key].push({ mimetype, data: base64String });
            }
        }

        console.log("Base64 files: ", base64Files);
        return { base64: base64Files };
    };

    const handleSave = async () => {
        console.log('Data to save:', helpData);
        setLoading(true);

        const { base64 } = await getBase64();

        try {
            await ApiService({
                method: "POST",
                endpoint: apiPaths.help,
                data: { ...helpData, base64 },
            });
            toast.success('Help Data saved successfully');
        } catch (error) {
            toast.error('Error saving Help data');
            console.error('Error saving data:', error);
        }
        finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (file) {
            const key = `contactUs[${index}].image`;
            formData.set(key, file); // Update or append file
            setFormData(formData); // Update state to trigger re-render (if necessary)

            try {
                const imageUrl = URL.createObjectURL(file);
                handleChange('contactUs', index, 'image', imageUrl);
            } catch (error) {
                console.error('Error uploading image:', error);
            }
        }
    };

    return {
        helpData,
        handleChange,
        handleAdd,
        handleDelete,
        handleSave,
        handleImageUpload,
        loading,
    };
};

export default useHelp;
